import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiService from "../services/apiService";
import toolsService from "../services/toolsService";

/**
 * LecturePlayer — Displays expanded lecture content for a lesson.
 *
 * Flow:
 * 1. Check localStorage for cached expanded slides
 * 2. If none, try to find matching file on LST server and expand
 * 3. If no match, let user upload a PDF/PPTX or select from LST server
 * 4. Store results in localStorage for instant reload
 */

const STORAGE_KEY = (id) => `lst_slides_${id}`;

function LecturePlayer() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [course, setCourse] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [expanding, setExpanding] = useState(false);
  const [expandStatus, setExpandStatus] = useState('');
  const [error, setError] = useState(null);
  const [lstFiles, setLstFiles] = useState([]);
  const [matchedFile, setMatchedFile] = useState(null);
  const [thumbnailDir, setThumbnailDir] = useState(null);
  const [playingAll, setPlayingAll] = useState(false);
  const [slideSearch, setSlideSearch] = useState('');

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const lessonData = await apiService.getLessonById(id);
        setLesson(lessonData);

        const courseData = await apiService.getCourseById(lessonData.course);
        setCourse(courseData);

        // Check localStorage for cached expanded slides
        const cached = localStorage.getItem(STORAGE_KEY(id));
        const cachedThumbDir = localStorage.getItem(STORAGE_KEY(id) + '_thumbdir');
        if (cached) {
          try {
            const parsed = JSON.parse(cached);
            if (parsed.length > 0) {
              setSlides(parsed);
              if (cachedThumbDir) setThumbnailDir(cachedThumbDir);
              setLoading(false);
              return;
            }
          } catch (e) {
            localStorage.removeItem(STORAGE_KEY(id));
          }
        }

        // Check if lesson has a DocResource (uploaded PPTX/PDF from IMSCC import)
        const docResources = lessonData.doc_resources || [];
        const slideDoc = docResources.find(r =>
          r.category === 'lesson' && r.filename && r.filename.match(/\.(pptx|pdf|ppt)$/i)
        );

        if (slideDoc) {
          // Upload the DocResource file to LST for processing
          console.log('Found lesson DocResource:', slideDoc.filename, slideDoc.resource_url);
          try {
            // Fetch the file from Azure via SAS URL
            const lessonWithSas = await apiService.getLessonByIdWithSas(id);
            const updatedDocs = (lessonWithSas.doc_resources || []).find(r => r.id === slideDoc.id);
            const sasUrl = updatedDocs?.resource_url || slideDoc.resource_url;

            // Download file and upload to LST
            const blobRes = await fetch(sasUrl);
            if (blobRes.ok) {
              const blob = await blobRes.blob();
              const formData = new FormData();
              formData.append('file', blob, slideDoc.filename);
              await apiService.uploadLstFile(formData);
              console.log('Uploaded DocResource to LST:', slideDoc.filename);
              setMatchedFile(slideDoc.filename);
            }
          } catch (e) {
            console.warn('Could not upload DocResource to LST:', e);
            // Fall through to LST file matching below
          }
        }

        // Check LST server for matching file (fallback if no DocResource)
        if (!slideDoc) {
          try {
            const uploadsData = await apiService.listLstUploads();
            const files = uploadsData.uploads || uploadsData.files || [];
            console.log('LST files found:', files.length, files);
            setLstFiles(files);

            const lessonTitle = (lessonData.title || '').toLowerCase().replace(/[()]/g, '');
            let bestMatch = null;
            let bestScore = 0;
            for (const f of files) {
              const fname = (typeof f === 'string' ? f : f.name || '').toLowerCase().replace(/[_()]/g, ' ').replace(/\.\w+$/, '');
              const titleWords = lessonTitle.split(/[\s\-:]+/).filter(w => w.length > 0);
              const matchCount = titleWords.filter(w => fname.includes(w)).length;
              if (matchCount > bestScore) {
                bestScore = matchCount;
                bestMatch = f;
              }
            }
            const matched = bestScore >= 3 ? bestMatch : null;

            if (matched) {
              const fname = typeof matched === 'string' ? matched : matched.name;
              console.log('Matched LST file:', fname);
              setMatchedFile(fname);
            }
          } catch (e) {
            console.warn('Could not fetch LST uploads:', e);
          }
        }
      } catch (err) {
        setError('Failed to load lesson: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [id]);

  const handleExpand = async (filename) => {
    if (!filename) return;
    setExpanding(true);
    setExpandStatus('');
    const lstBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
    try {
      // Step 1: Extract
      setExpandStatus('Extracting slides from ' + filename + '...');
      console.log('[LST] Extracting:', filename);
      const extractResult = await toolsService.extractSlides(filename);
      console.log('[LST] Extract result:', extractResult);
      const slidesJson = extractResult.slides_json;
      const thumbDir = extractResult.thumbnail_dir;
      if (thumbDir) {
        setThumbnailDir(thumbDir);
        localStorage.setItem(STORAGE_KEY(id) + '_thumbdir', thumbDir);
      }

      if (!slidesJson) {
        setExpandStatus('Extraction returned no data.');
        setExpanding(false);
        return;
      }

      // Step 2: Expand
      const totalSlides = extractResult.total_slides || '?';
      setExpandStatus(`AI expanding ${totalSlides} slides... (this may take 2-5 minutes)`);
      console.log('[LST] Expanding:', slidesJson);
      const expandResult = await toolsService.expandNotes(slidesJson);
      console.log('[LST] Expand result:', expandResult);
      const expandedJson = expandResult.expanded_json;

      if (!expandedJson) {
        setExpandStatus('Expansion returned no data.');
        setExpanding(false);
        return;
      }

      // Step 3: Generate narration
      let finalJson = expandedJson;
      let narrationJson = null;
      try {
        setExpandStatus('Generating professor narration...');
        console.log('[LST] Narrating:', expandedJson);
        const narrationResult = await toolsService.generateNarration(expandedJson);
        console.log('[LST] Narration result:', narrationResult);
        if (narrationResult.narration_json) {
          narrationJson = narrationResult.narration_json;
          finalJson = narrationJson;
        }
      } catch (narErr) {
        console.warn('[LST] Narration failed, using expanded only:', narErr.message);
        // Continue with expanded JSON if narration fails
      }

      // Step 3b: Lecture flow + TTS MP3 (background, non-blocking for display)
      if (narrationJson) {
        try {
          setExpandStatus('Generating audio narration (MP3)...');
          const flowResult = await toolsService.generateLectureFlow(narrationJson);
          console.log('[LST] Lecture flow result:', flowResult);
          if (flowResult.lecture_flow_json) {
            const ttsResult = await toolsService.generateTts(flowResult.lecture_flow_json);
            console.log('[LST] TTS result:', ttsResult);
          }
        } catch (ttsErr) {
          console.warn('[LST] TTS failed, audio will not play:', ttsErr.message);
        }
      }

      // Step 4: Fetch the final JSON file
      setExpandStatus('Loading expanded content...');
      const fetchUrl = `${lstBase}/api/lst/output/${finalJson}`;
      console.log('[LST] Fetching:', fetchUrl);
      const jsonRes = await fetch(fetchUrl);
      console.log('[LST] Fetch status:', jsonRes.status);

      if (!jsonRes.ok) {
        setExpandStatus(`Failed to load output file (${jsonRes.status}). File: ${expandedJson}`);
        setExpanding(false);
        return;
      }

      const data = await jsonRes.json();
      console.log('[LST] Data keys:', Object.keys(data));
      let expandedSlides = data.expanded_slides || data.narrated_slides || data.slides || [];
      if (!Array.isArray(expandedSlides)) expandedSlides = [];
      console.log('[LST] Slides loaded:', expandedSlides.length);

      if (expandedSlides.length > 0) {
        expandedSlides = expandedSlides.map((s, i) => ({
          number: s.slide_number || s.number || i + 1,
          title: s.title || s.slide_title || `Slide ${i + 1}`,
          detailed_notes: s.detailed_notes || s.expanded_content || s.content || s.original_text || '',
          key_info: s.key_info || s.key_points || s.key_concepts || s.clinical_relevance || '',
          study_tips: s.study_tips || s.tips || '',
          narration_text: s.narration_text || s.narration || s.professor_narration || '',
          duration_sec: s.duration_sec || s.estimated_duration || 30,
        }));
        setSlides(expandedSlides);
        localStorage.setItem(STORAGE_KEY(id), JSON.stringify(expandedSlides));
        setExpandStatus('');
      } else {
        console.warn('[LST] No slides in data. Keys:', Object.keys(data));
        setExpandStatus('Expansion completed but no slides found in output.');
      }
    } catch (err) {
      console.error('[LST] Expansion failed:', err);
      const errMsg = err.response?.data?.error || err.response?.data?.detail || err.message || 'Unknown error';
      setExpandStatus('Expansion failed: ' + errMsg);
    } finally {
      setExpanding(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setExpanding(true);
    setExpandStatus('Uploading ' + file.name + '...');
    try {
      const uploadResult = await toolsService.uploadSlides(file);
      const uploadedName = uploadResult.filename || file.name;
      setMatchedFile(uploadedName);
      await handleExpand(uploadedName);
    } catch (err) {
      setExpandStatus('Upload failed: ' + err.message);
      setExpanding(false);
    }
  };

  const [autoAdvance, setAutoAdvance] = useState(true);
  const slide = slides[currentSlide] || {};
  const goToSlide = (idx) => { if (idx >= 0 && idx < slides.length) setCurrentSlide(idx); };
  const hasExpandedContent = slides.length > 0;

  const audioRef = useRef(null);

  const handleAudioEnded = () => {
    if ((autoAdvance || playingAll) && currentSlide < slides.length - 1) {
      setTimeout(() => {
        setCurrentSlide(prev => prev + 1);
        // Auto-play next audio after slide change
        if (playingAll) {
          setTimeout(() => {
            const audio = document.querySelector('audio');
            if (audio) audio.play().catch(() => {});
          }, 300);
        }
      }, 500);
    } else if (playingAll) {
      setPlayingAll(false);
    }
  };

  const handlePlayAll = () => {
    setPlayingAll(true);
    setAutoAdvance(true);
    const audio = document.querySelector('audio');
    if (audio) audio.play().catch(() => {});
  };

  const handleStopAll = () => {
    setPlayingAll(false);
    const audio = document.querySelector('audio');
    if (audio) audio.pause();
  };

  const handleKeyDown = useCallback((e) => {
    if (!hasExpandedContent) return;
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        setCurrentSlide(prev => Math.max(0, prev - 1));
        break;
      case 'ArrowRight':
        e.preventDefault();
        setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1));
        break;
      case ' ':
        e.preventDefault();
        const audio = document.querySelector('audio');
        if (audio) { audio.paused ? audio.play() : audio.pause(); }
        break;
      default:
        break;
    }
  }, [hasExpandedContent, slides.length]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>Loading lesson data...</div>;
  if (error) return <div style={{ padding: '20px', color: '#dc3545' }}>{error}</div>;

  return (
    <div>
      <button className="btn btn-secondary" onClick={() => navigate(-1)} style={{ marginBottom: '16px' }}>
        &larr; Back to Course
      </button>

      {/* Breadcrumb */}
      <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
        <span style={{ cursor: 'pointer', color: '#042F54' }} onClick={() => navigate('/courses')}>My Courses</span>
        <span style={{ margin: '0 6px' }}>/</span>
        <span style={{ cursor: 'pointer', color: '#042F54' }} onClick={() => navigate(`/course/${lesson?.course}`)}>{course?.title}</span>
        <span style={{ margin: '0 6px' }}>/</span>
        <span style={{ fontWeight: 600, color: '#042F54' }}>{lesson?.title}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ marginBottom: '4px' }}>{lesson?.title}</h2>
          <p style={{ color: '#888', fontSize: '14px', margin: 0 }}>
            {course?.title} {hasExpandedContent ? `\u00B7 ${slides.length} expanded slides` : ''}
          </p>
        </div>
        {hasExpandedContent && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button className="btn btn-secondary" onClick={() => {
              localStorage.removeItem(STORAGE_KEY(id));
              localStorage.removeItem(STORAGE_KEY(id) + '_thumbdir');
              setSlides([]);
              setCurrentSlide(0);
              setThumbnailDir(null);
            }}>Clear Cache</button>
            <button className="btn btn-primary" onClick={() => {
              const json = JSON.stringify(slides, null, 2);
              const blob = new Blob([json], { type: 'application/json' });
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `expanded_slides_${lesson?.title?.replace(/\s+/g, '_') || id}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}>Download Slides JSON</button>
          </div>
        )}
      </div>

      {expanding && (() => {
        const steps = ['Extracting', 'AI expanding', 'Generating professor', 'Generating audio', 'Loading'];
        const currentStep = steps.findIndex(s => expandStatus.toLowerCase().includes(s.toLowerCase()));
        const pct = currentStep >= 0 ? Math.round(((currentStep + 1) / (steps.length + 1)) * 100) : 10;
        return (
          <div className="card" style={{ padding: '20px', marginBottom: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: '14px', color: '#042F54', fontWeight: 600, marginBottom: '8px' }}>{expandStatus}</div>
            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', marginBottom: '8px' }}>
              {steps.map((s, i) => (
                <div key={i} style={{
                  width: '80px', height: '4px', borderRadius: '2px',
                  background: i <= currentStep ? '#F56A3F' : '#e2e8f0',
                  transition: 'background 0.3s',
                }} />
              ))}
            </div>
            <div style={{ fontSize: '12px', color: '#888' }}>Step {Math.max(currentStep + 1, 1)} of {steps.length} ({pct}%)</div>
          </div>
        );
      })()}

      {!hasExpandedContent && !expanding && (
        <div className="card" style={{ padding: '30px' }}>
          {matchedFile ? (
            <div>
              <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                Found matching file on Lecture Slide Tool: <strong>{matchedFile}</strong>
              </p>
              <button className="btn btn-primary" onClick={() => handleExpand(matchedFile)}>
                Expand This File
              </button>
            </div>
          ) : (
            <div>
              <p style={{ fontSize: '14px', color: '#666', marginBottom: '16px' }}>
                No expanded lecture content yet. Upload a PDF or PPTX, or select a file from the Lecture Slide Tool server.
              </p>
              {lstFiles.length > 0 && (
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>Available files on LST server:</p>
                  <select
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #ccc', fontSize: '13px', maxWidth: '400px', width: '100%' }}
                    onChange={(e) => { if (e.target.value) { setMatchedFile(e.target.value); handleExpand(e.target.value); }}}
                    defaultValue=""
                  >
                    <option value="">-- Select a file to expand --</option>
                    {lstFiles.map((f, i) => {
                      const name = typeof f === 'string' ? f : f.name || '';
                      return <option key={i} value={name}>{name}</option>;
                    })}
                  </select>
                </div>
              )}
              <label className="btn btn-primary" style={{ cursor: 'pointer', display: 'inline-block' }}>
                Upload PDF/PPTX
                <input type="file" accept=".pdf,.pptx,.ppt" onChange={handleFileUpload} style={{ display: 'none' }} />
              </label>
            </div>
          )}
          {expandStatus && <p style={{ color: '#dc3545', marginTop: '12px', fontSize: '13px' }}>{expandStatus}</p>}
        </div>
      )}

      {hasExpandedContent && !expanding && (
        <div style={{ display: 'flex', gap: '16px' }}>
          <div style={{ width: '220px', flexShrink: 0 }}>
            <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', background: '#042F54', color: '#fff', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Slides ({slides.length})
              </div>
              <div style={{ padding: '6px 8px', borderBottom: '1px solid #e2e8f0' }}>
                <input
                  type="text"
                  placeholder="Search slides..."
                  value={slideSearch}
                  onChange={(e) => setSlideSearch(e.target.value)}
                  style={{ width: '100%', padding: '4px 8px', fontSize: '12px', border: '1px solid #ddd', borderRadius: '4px', outline: 'none' }}
                />
              </div>
              {(() => {
                const filtered = slides.filter(s => !slideSearch || (s.title || '').toLowerCase().includes(slideSearch.toLowerCase()) || (s.detailed_notes || '').toLowerCase().includes(slideSearch.toLowerCase()));
                // Group consecutive slides with same title
                const groups = [];
                filtered.forEach(s => {
                  const idx = slides.indexOf(s);
                  const lastGroup = groups[groups.length - 1];
                  if (lastGroup && lastGroup.title === s.title) {
                    lastGroup.slides.push({ slide: s, index: idx });
                  } else {
                    groups.push({ title: s.title, slides: [{ slide: s, index: idx }] });
                  }
                });

                return groups.map((group, gi) => {
                  if (group.slides.length === 1) {
                    const { slide: s, index: idx } = group.slides[0];
                    return (
                      <div key={idx} onClick={() => goToSlide(idx)} style={{
                        padding: '8px 16px', cursor: 'pointer', fontSize: '13px',
                        borderBottom: '1px solid #f0f0f0',
                        background: idx === currentSlide ? '#f0f4ff' : '#fff',
                        borderLeft: idx === currentSlide ? '3px solid #042F54' : '3px solid transparent',
                        color: idx === currentSlide ? '#042F54' : '#555',
                        fontWeight: idx === currentSlide ? 600 : 400,
                      }}>
                        <span style={{ color: '#F56A3F', fontWeight: 700, marginRight: '8px' }}>{s.number}</span>
                        {(s.title || '').substring(0, 35)}{(s.title || '').length > 35 ? '...' : ''}
                      </div>
                    );
                  }
                  // Grouped slides
                  const isActiveGroup = group.slides.some(gs => gs.index === currentSlide);
                  return (
                    <div key={`g${gi}`} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <div style={{
                        padding: '8px 16px', fontSize: '13px', fontWeight: 600,
                        background: isActiveGroup ? '#f0f4ff' : '#fafafa',
                        color: '#042F54',
                        borderLeft: isActiveGroup ? '3px solid #042F54' : '3px solid transparent',
                      }}>
                        {(group.title || '').substring(0, 30)}{(group.title || '').length > 30 ? '...' : ''}
                        <span style={{ fontSize: '11px', fontWeight: 400, color: '#888', marginLeft: '6px' }}>({group.slides.length})</span>
                      </div>
                      {group.slides.map(({ slide: s, index: idx }) => (
                        <div key={idx} onClick={() => goToSlide(idx)} style={{
                          padding: '4px 16px 4px 32px', cursor: 'pointer', fontSize: '12px',
                          background: idx === currentSlide ? '#e8f0fe' : '#fff',
                          color: idx === currentSlide ? '#042F54' : '#888',
                          fontWeight: idx === currentSlide ? 600 : 400,
                        }}>
                          <span style={{ color: '#F56A3F', fontWeight: 700, marginRight: '6px' }}>{s.number}</span>
                          Part {group.slides.indexOf(group.slides.find(gs => gs.index === idx)) + 1}
                        </div>
                      ))}
                    </div>
                  );
                });
              })()}
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ background: '#042F54', color: '#fff', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 }}>
                  Slide {slide.number || currentSlide + 1} of {slides.length}
                </span>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-secondary" disabled={currentSlide === 0} onClick={() => goToSlide(currentSlide - 1)} style={{ padding: '4px 12px', fontSize: '13px' }}>Prev</button>
                  <button className="btn btn-secondary" disabled={currentSlide >= slides.length - 1} onClick={() => goToSlide(currentSlide + 1)} style={{ padding: '4px 12px', fontSize: '13px' }}>Next</button>
                  {!playingAll ? (
                    <button className="btn" style={{ background: '#28a745', color: '#fff', padding: '4px 12px', fontSize: '13px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} onClick={handlePlayAll}>Play All</button>
                  ) : (
                    <button className="btn" style={{ background: '#dc3545', color: '#fff', padding: '4px 12px', fontSize: '13px', border: 'none', borderRadius: '6px', cursor: 'pointer' }} onClick={handleStopAll}>Stop</button>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#888', marginLeft: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" checked={autoAdvance} onChange={(e) => setAutoAdvance(e.target.checked)} style={{ accentColor: '#F56A3F' }} />
                    Auto
                  </label>
                </div>
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', color: '#042F54' }}>{slide.title}</h3>
            </div>

            {/* Original slide image - full width */}
            {thumbnailDir && (
              <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#042F54', marginBottom: '8px' }}>Original Slide</div>
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/lst/thumbs/${thumbnailDir}/slide_${String(slide.number || currentSlide + 1).padStart(3, '0')}.png`}
                  alt={`Slide ${slide.number || currentSlide + 1}`}
                  style={{ width: '100%', maxWidth: '960px', borderRadius: '6px', border: '1px solid #e2e8f0', display: 'block', margin: '0 auto' }}
                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                />
              </div>
            )}

            {/* Expanded content */}
            <div className="card" style={{ padding: '20px', marginBottom: '16px' }}>
              <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#F56A3F', marginBottom: '8px' }}>Expanded Content</div>
              <div style={{ fontSize: '14px', lineHeight: 1.7, color: '#333', whiteSpace: 'pre-wrap' }}>
                {slide.detailed_notes || 'No content available.'}
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
              <div className="card" style={{ padding: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#F56A3F', marginBottom: '6px' }}>Key Information</div>
                <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#555', whiteSpace: 'pre-wrap' }}>{slide.key_info || '\u2014'}</div>
              </div>
              <div className="card" style={{ padding: '16px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#F56A3F', marginBottom: '6px' }}>Study Tips</div>
                <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#555', whiteSpace: 'pre-wrap' }}>{slide.study_tips || '\u2014'}</div>
              </div>
            </div>

            {/* Audio player + Narration */}
            {thumbnailDir && (() => {
              const audioDir = 'tts_audio_' + thumbnailDir.replace(/^slides_/, '');
              const slideNum = String(slide.number || currentSlide + 1).padStart(3, '0');
              const audioUrl = `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/lst/audio/${audioDir}/slide_${slideNum}.mp3`;
              return (
                <div className="card" style={{ padding: '16px', borderLeft: '4px solid #042F54', marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#042F54', marginBottom: '8px' }}>Audio Narration</div>
                  <audio controls onEnded={handleAudioEnded} style={{ width: '100%', marginBottom: slide.narration_text ? '12px' : '0' }} key={audioUrl}>
                    <source src={audioUrl} type="audio/mpeg" />
                    Your browser does not support audio playback.
                  </audio>
                  {slide.narration_text && (
                    <div>
                      <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', marginBottom: '4px' }}>Script</div>
                      <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#555', whiteSpace: 'pre-wrap' }}>{slide.narration_text}</div>
                    </div>
                  )}
                </div>
              );
            })()}
            {!thumbnailDir && slide.narration_text && (
              <div className="card" style={{ padding: '16px', borderLeft: '4px solid #042F54' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#042F54', marginBottom: '6px' }}>Narration Script</div>
                <div style={{ fontSize: '13px', lineHeight: 1.6, color: '#555', whiteSpace: 'pre-wrap' }}>{slide.narration_text}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LecturePlayer;
